import type {
  FlowNodeEntity,
  FreeLayoutPluginContext,
  WorkflowNodeEntity,
} from '@flowgram.ai/free-layout-editor';
import { FlowNodeBaseType, WorkflowNodeLinesData } from '@flowgram.ai/free-layout-editor';
import type { FlowNodeData, WorkflowTeamsUploadLocation } from '$/domains/workFlowV3/type';
import useFlowTeamStore, { getEffectiveNodeCost } from '$/store/useTeamFlowStore';
import { openVipModal } from '$/store/useVipModalStore';
import { getUrlSearchParams } from '$/utils';
import { mtstatTrack } from '$/utils/mtstat';
import type { PostTaskConfig } from '../config/postTask.config';
import { TVFlowNodeNextTypeEnum } from '../config/node.config';
import type { GroupNodeSavePayload } from './collectGroupNodePayload';
import { getMcpInfoParameterValue, type ContentSource } from './index';
import { resolvePostTaskFuncRunTrackTab, resolveFlowFuncRunInputText } from './postTaskTracking';

export type WorkflowGroupClickType =
  | 'group'
  | 'un_group'
  | 'group_storyboard'
  | 'un_group_storyboard';

export interface FlowNodeTrackAction {
  key: string;
  label: string;
}

export type FlowNodeBarTrackOverrides = Partial<{
  node_type: string;
  content_urls: string;
  task_id: string;
  sub_task_id: string;
}> & Record<string, unknown>;

/** 节点编辑栏 func_id 与展示名映射，func_name 上报使用 UI 文案。 */
export const FLOW_NODE_BAR_FUNC_NAMES: Record<string, string> = {
  change_to_hd: '高清',
  change_super_hd: '超清',
  change_ai_hd: 'AI超清',
  change_4k_video: '4K',
  change_ai_4k_video: 'AI 4K+',
  remove_aimark: '智能去字幕',
  cut_out: '抠图',
  multi_angle: '多角度',
  lighting: '打光',
  grid_split: '宫格切分',
  label: 'label',
  download: '下载',
  add_asset_library: '添加到资产库',
  regenerate: '重新生成',
  storyboard: '生成分镜',
  batch_generate_video: '批量生成视频',
  video_compositing: '视频合成',
};

/**
 * 解析节点编辑栏 func_name：优先显式传入，其次字典，最后回退 func_id。
 */
export function resolveFlowNodeBarFuncName(funcId: string, funcName?: string) {
  if (funcName) return funcName;
  return FLOW_NODE_BAR_FUNC_NAMES[funcId] ?? funcId;
}

/**
 * 读取当前工作流房间 ID，并把 useTeamFlowStore 的历史类型问题隔离在埋点工具内。
 */
function getFlowTeamRoomId() {
  return (useFlowTeamStore as unknown as {
    getState: () => {
      roomId: string;
    };
  }).getState().roomId;
}

/**
 * 读取工作流 VIP 弹窗 room_id：优先 store，其次 URL 查询参数。
 */
function getWorkflowVipModalRoomId() {
  const storeRoomId = getFlowTeamRoomId();
  if (storeRoomId) return storeRoomId;
  if (typeof window === 'undefined') return '';
  return getUrlSearchParams().get('room_id') || '';
}

/**
 * 从节点 media_list 中提取埋点字段，支持不同节点的 url/originUrl/uri 结构。
 */
function collectMediaValues(
  mediaList: FlowNodeData['media_list'] | undefined,
  keys: string[],
) {
  if (!Array.isArray(mediaList)) return '';
  return mediaList
    .map((item) => {
      const media = item as Record<string, unknown>;
      return keys.map((key) => media[key]).find(Boolean);
    })
    .filter(Boolean)
    .map(String)
    .join(',');
}

/**
 * 从群组节点收集子节点 id 列表。
 */
export function collectGroupChildNodeIds(groupNode: WorkflowNodeEntity | FlowNodeEntity): string[] {
  return (groupNode.blocks ?? []).map((node) => node.id);
}

/**
 * 上报打组/解组/分镜组相关点击埋点。
 */
export function trackWorkflowGroupClick(
  clickType: WorkflowGroupClickType,
  options: {
    groupId?: string;
    nodeIds: string[];
  },
) {
  mtstatTrack('roboneo_workflow_group_click', {
    room_id: getFlowTeamRoomId(),
    group_id: options.groupId ?? '',
    node_list: options.nodeIds.join(','),
    click_type: clickType,
  });
}

const WORKFLOW_NODE_TRACK_AGENT_TYPE = 'agent_teams';

export type WorkflowUploadControlTriggerType = 'click' | 'drag_drop';

/** 大禹 #7493：3.0 工作流 upload_control_click 上传位置（loaction 枚举）。 */
export type WorkflowUploadControlLocation = WorkflowTeamsUploadLocation;

/**
 * 上报 3.0 工作流上传控件点击/拖拽（upload_control_click）。
 */
export function trackUploadControlClick(options: {
  triggerType: WorkflowUploadControlTriggerType;
  uploadLocation: WorkflowUploadControlLocation;
  nodeId?: string;
}) {
  const { triggerType, uploadLocation, nodeId } = options;

  mtstatTrack('upload_control_click', {
    room_id: getFlowTeamRoomId(),
    trigger_type: triggerType,
    location: 'canvas_teams',
    loaction: uploadLocation,
    ...(nodeId ? { node_id: nodeId } : {}),
  });
}

/**
 * 从 inputNodeId 或真实入边解析指定类型的上游节点 id。
 */
function resolvePauseInputNodeIds(
  node: FlowNodeEntity,
  dataNodeIds: string[] | undefined,
  nodeType: TVFlowNodeNextTypeEnum,
): string[] {
  if (dataNodeIds?.length) {
    return dataNodeIds;
  }
  const inputNodes: FlowNodeEntity[] = node.getData(WorkflowNodeLinesData)?.inputNodes ?? [];
  return inputNodes
    .filter((inputNode) => inputNode.flowNodeType === nodeType)
    .map((inputNode) => inputNode.id);
}

/**
 * 读取上游输入节点的首个媒体 URL，供 workflow_pause_click.media_url 使用。
 */
function collectPauseInputNodeUrl(
  ctx: FreeLayoutPluginContext,
  nodeId: string,
): string {
  const inputNode = ctx.document.getNode(nodeId);
  const media = (inputNode?.toJSON()?.data as FlowNodeData | undefined)?.media_list?.[0] as Record<string, unknown> | undefined;
  if (!media) return '';
  const url = media.url || media.originUrl || media.uri || media.value;
  return typeof url === 'string' ? url : '';
}

/**
 * 从节点 mcpInfo 与上游文本节点汇总暂停埋点用的 prompt 文案。
 */
function resolveWorkflowPausePromptText(
  ctx: FreeLayoutPluginContext,
  node: FlowNodeEntity,
  nodeData: FlowNodeData,
  textNodeIds: string[],
): string {
  const promptFromMcp = getMcpInfoParameterValue(node, 'prompt') ?? '';
  if (promptFromMcp.trim()) {
    return promptFromMcp;
  }

  const textValues = textNodeIds
    .flatMap((id) => {
      const inputNodeData = ctx.document.getNode(id)?.toJSON()?.data as FlowNodeData | undefined;
      return inputNodeData?.media_list ?? [];
    })
    .map((item) => (item as Record<string, unknown>).value)
    .filter((value): value is string => typeof value === 'string' && value.trim() !== '');

  if (textValues.length) {
    return textValues.join('');
  }

  const fallbackPrompt = nodeData.mcpInfo?.parameters?.prompt;
  return typeof fallbackPrompt === 'string' ? fallbackPrompt : '';
}

/**
 * 根据取消中的画布节点，组装 workflow_pause_click 所需上下文参数。
 */
export function buildWorkflowPauseClickTrackParams(
  ctx: FreeLayoutPluginContext,
  node: FlowNodeEntity,
): {
    promptText: string;
    mediaUrl: string;
    imageNum: number;
    messageType: string;
    fileNum: number;
    videoNum: number;
    nodeList: string;
  } {
  const nodeData = node.toJSON().data as FlowNodeData;
  const textNodeIds = resolvePauseInputNodeIds(
    node,
    nodeData.inputNodeId?.textNodeIds,
    TVFlowNodeNextTypeEnum.TEXT_NODE,
  );
  const imageNodeIds = resolvePauseInputNodeIds(
    node,
    nodeData.inputNodeId?.imageNodeIds,
    TVFlowNodeNextTypeEnum.IMAGE_NODE,
  );
  const videoNodeIds = resolvePauseInputNodeIds(
    node,
    nodeData.inputNodeId?.videoNodeIds,
    TVFlowNodeNextTypeEnum.VIDEO_NODE,
  );
  const audioNodeIds = resolvePauseInputNodeIds(
    node,
    nodeData.inputNodeId?.audioNodeIds,
    TVFlowNodeNextTypeEnum.AUDIO_NODE,
  );
  const promptText = resolveWorkflowPausePromptText(ctx, node, nodeData, textNodeIds);
  const messageTypes: string[] = [];

  if (promptText.trim()) {
    messageTypes.push('prompt');
  }
  if (textNodeIds.length) {
    messageTypes.push('text');
  }
  if (imageNodeIds.length) {
    messageTypes.push('image');
  }
  if (videoNodeIds.length) {
    messageTypes.push('video');
  }
  if (audioNodeIds.length) {
    messageTypes.push('file');
  }

  const nodeList = [
    ...textNodeIds,
    ...imageNodeIds,
    ...videoNodeIds,
    ...audioNodeIds,
  ];
  const mediaUrl = nodeList
    .map((id) => collectPauseInputNodeUrl(ctx, id))
    .filter(Boolean)
    .join(',');

  return {
    promptText,
    mediaUrl,
    imageNum: imageNodeIds.length,
    videoNum: videoNodeIds.length,
    fileNum: audioNodeIds.length,
    messageType: messageTypes.join(','),
    nodeList: nodeList.join(','),
  };
}

/**
 * 上报工作流对话暂停 / 取消生成弹窗点击确定（workflow_pause_click）。
 */
export function trackWorkflowPauseClick(options: {
  pauseDuration: number;
  ctx?: FreeLayoutPluginContext;
  node?: FlowNodeEntity;
  promptText?: string;
  mediaUrl?: string;
  imageNum?: number;
  messageType?: string;
  fileNum?: number;
  videoNum?: number;
  nodeList?: string;
}) {
  const resolved = options.ctx && options.node
    ? buildWorkflowPauseClickTrackParams(options.ctx, options.node)
    : undefined;

  mtstatTrack('workflow_pause_click', {
    room_id: getFlowTeamRoomId(),
    agent_type: WORKFLOW_NODE_TRACK_AGENT_TYPE,
    pause_duration: options.pauseDuration,
    prompt_text: options.promptText ?? resolved?.promptText ?? '',
    media_url: options.mediaUrl ?? resolved?.mediaUrl ?? '',
    image_num: options.imageNum ?? resolved?.imageNum ?? 0,
    message_type: options.messageType ?? resolved?.messageType ?? '',
    file_num: options.fileNum ?? resolved?.fileNum ?? 0,
    video_num: options.videoNum ?? resolved?.videoNum ?? 0,
    node_list: options.nodeList ?? resolved?.nodeList ?? '',
  });
}

/**
 * 上报任务点击【取消】出现取消弹窗（workflow_pause_pop_click）。
 */
export function trackWorkflowPausePopClick(options: {
  pauseDuration: number;
  taskId: string;
  rate: number;
}) {
  mtstatTrack('workflow_pause_pop_click', {
    room_id: getFlowTeamRoomId(),
    agent_type: WORKFLOW_NODE_TRACK_AGENT_TYPE,
    pause_duration: options.pauseDuration,
    task_id: options.taskId,
    rate: options.rate,
  });
}

/**
 * 构建 workflow_node_copy / workflow_node_delete / workflow_node_save 公共参数。
 * 单节点带 content_type，多节点仅 node_id（与大禹 #7493 口径一致）。
 */
function buildWorkflowNodeActionTrackParams(nodes: WorkflowNodeEntity[]) {
  const nodeIds = nodes.map((node) => node.id).join(',');
  if (nodes.length === 1) {
    return {
      node_id: nodeIds,
      content_type: nodes[0].flowNodeType,
    };
  }
  return { node_id: nodeIds };
}

/**
 * 上报节点复制埋点。
 */
export function trackWorkflowNodeCopy(nodes: WorkflowNodeEntity[]) {
  if (!nodes.length) return;
  mtstatTrack('workflow_node_copy', {
    room_id: getFlowTeamRoomId(),
    agent_type: WORKFLOW_NODE_TRACK_AGENT_TYPE,
    ...buildWorkflowNodeActionTrackParams(nodes),
  });
}

/**
 * 上报创建副本按钮点击；多选节点字段按选择顺序使用逗号分隔。
 */
export function trackWorkflowCreateCopyClick(nodes: WorkflowNodeEntity[]) {
  if (!nodes.length) return;
  const mediaUrl = nodes
    .map((node) => collectMediaValues(
      (node.toJSON()?.data as FlowNodeData | undefined)?.media_list,
      ['url', 'originUrl', 'uri', 'value'],
    ))
    .filter(Boolean)
    .join(',');

  mtstatTrack('roboneo_create_copy_click', {
    room_id: getFlowTeamRoomId(),
    node_id: nodes.map((node) => node.id).join(','),
    node_type: nodes.map((node) => node.flowNodeType).join(','),
    media_url: mediaUrl,
  });
}

/**
 * 上报节点删除埋点。
 */
export function trackWorkflowNodeDelete(nodes: WorkflowNodeEntity[]) {
  if (!nodes.length) return;
  mtstatTrack('workflow_node_delete', {
    room_id: getFlowTeamRoomId(),
    agent_type: WORKFLOW_NODE_TRACK_AGENT_TYPE,
    ...buildWorkflowNodeActionTrackParams(nodes),
  });
}

/**
 * 上报节点收藏（保存到我的节点）点击埋点。
 */
export function trackWorkflowNodeSave(nodes: WorkflowNodeEntity[]) {
  if (!nodes.length) return;
  mtstatTrack('workflow_node_save', {
    room_id: getFlowTeamRoomId(),
    agent_type: WORKFLOW_NODE_TRACK_AGENT_TYPE,
    ...buildWorkflowNodeActionTrackParams(nodes),
  });
}

/**
 * 从收藏 payload 中提取节点 id 列表。
 */
export function collectMyNodeAddNodeIds(payload: GroupNodeSavePayload): string[] {
  return payload.nodes.map((node) => node.id).filter(Boolean);
}

/**
 * 解析「添加到我的节点」场景下的 group_id：单选群组节点时取组 id，否则为空。
 */
export function resolveMyNodeAddGroupId(nodes: FlowNodeEntity[]): string {
  if (nodes.length === 1 && nodes[0].toJSON().type === FlowNodeBaseType.GROUP) {
    return nodes[0].id;
  }
  return '';
}

/**
 * 汇总「添加到我的节点」公共埋点参数。
 */
function getMyNodeAddTrackParams(options: {
  nodeIds: string[];
  groupId?: string;
  coverUrl?: string;
  mynodeName?: string;
}) {
  return {
    room_id: getFlowTeamRoomId(),
    node_id_list: options.nodeIds.join(','),
    group_id: options.groupId ?? '',
    ...(options.coverUrl !== undefined ? { cover_url: options.coverUrl } : {}),
    ...(options.mynodeName !== undefined ? { mynode_name: options.mynodeName } : {}),
  };
}

/**
 * 上报「添加到我的节点」点击；入口按钮每点击一次上报一次。
 */
export function trackMyNodeAddClick(options: {
  nodeIds: string[];
  groupId?: string;
}) {
  mtstatTrack('roboneo_mynode_add_click', getMyNodeAddTrackParams(options));
}

/**
 * 上报「添加到我的节点」收藏成功。
 */
export function trackMyNodeAddSuccess(options: {
  nodeIds: string[];
  groupId?: string;
  coverUrl: string;
  mynodeName: string;
}) {
  mtstatTrack('roboneo_mynode_add_success', getMyNodeAddTrackParams(options));
}

/**
 * 汇总节点编辑栏公共参数，其他节点可通过 overrides 补充或覆盖特定字段。
 */
export function getFlowNodeBarTrackParams(
  node: FlowNodeEntity,
  overrides: FlowNodeBarTrackOverrides = {},
) {
  const data = node.toJSON?.()?.data as FlowNodeData | undefined;

  return {
    room_id: getFlowTeamRoomId(),
    node_id: node.id,
    node_type: node.flowNodeType,
    content_urls: collectMediaValues(data?.media_list, ['url', 'originUrl', 'uri']),
    task_id: collectMediaValues(data?.media_list, ['task_id']),
    sub_task_id: collectMediaValues(data?.media_list, ['sub_task_id']),
    ...overrides,
  };
}

/**
 * 上报节点快速开始词条曝光；一个词条曝光上报一次。
 */
export function trackFlowNodeFastStartShow(
  node: FlowNodeEntity,
  actions: readonly FlowNodeTrackAction[],
) {
  actions.forEach((action) => {
    mtstatTrack('roboneo_fast_start_show', {
      room_id: getFlowTeamRoomId(),
      node_id: node.id,
      content: action.label,
      content_id: action.key,
      node_type: node.flowNodeType,
    });
  });
}

/**
 * 上报节点快速开始词条点击。
 */
export function trackFlowNodeFastStartClick(
  node: FlowNodeEntity,
  action: FlowNodeTrackAction,
) {
  mtstatTrack('roboneo_fast_start_click', {
    room_id: getFlowTeamRoomId(),
    node_id: node.id,
    content: action.label,
    content_id: action.key,
    node_type: node.flowNodeType,
  });
}

/**
 * 上报节点编辑栏曝光。
 */
export function trackFlowNodeBarShow(
  node: FlowNodeEntity,
  overrides?: FlowNodeBarTrackOverrides,
) {
  mtstatTrack('roboneo_node_bar_show', getFlowNodeBarTrackParams(node, overrides));
}

/**
 * 上报节点编辑栏操作点击，func_name 与 func_id 成对上报便于数据侧展示。
 */
export function trackFlowNodeBarClick(
  node: FlowNodeEntity,
  funcId: string,
  funcName?: string,
  overrides?: FlowNodeBarTrackOverrides,
) {
  mtstatTrack('roboneo_node_bar_click', {
    ...getFlowNodeBarTrackParams(node, overrides),
    func_id: funcId,
    func_name: resolveFlowNodeBarFuncName(funcId, funcName),
  });
}

/**
 * 解析后链路功能对应的稳定 tab 枚举；未配置的新旧能力回退原展示名。
 */
export function resolvePostTaskFuncTabName(
  config: PostTaskConfig,
  parameters: Record<string, unknown> = {},
  fallbackName?: string,
): string {
  const { api_name: apiName } = config;
  const effectId = String(parameters.effect_id ?? config.parameters?.effect_id ?? '');
  const trackingTab = resolvePostTaskFuncRunTrackTab(config, parameters);

  if (trackingTab) {
    return trackingTab;
  }

  if (apiName === 'video_enhance') {
    return resolveFlowNodeBarFuncName('change_to_hd');
  }
  if (apiName === 'video_subtitle_remove') {
    return resolveFlowNodeBarFuncName('remove_aimark');
  }
  if (apiName === 'image_enhance') {
    return resolveFlowNodeBarFuncName('change_to_hd');
  }
  if (apiName === 'api_v1_sod_async') {
    return resolveFlowNodeBarFuncName('cut_out');
  }
  if (apiName === 'image_weave_anything2img') {
    if (effectId === '491890') return resolveFlowNodeBarFuncName('lighting');
    if (effectId === '491929') return resolveFlowNodeBarFuncName('multi_angle');
    if (['492413', '493062', '493063'].includes(effectId)) return '720°全景图';
  }
  return fallbackName ?? config.nodeName;
}

/**
 * 组装宫格切分发送埋点的 params_json 字段。
 */
export function buildGridSplitFuncRunParams(
  spec: { rows: number; cols: number; },
  cells: Array<{ row: number; col: number; }>,
): Record<string, string> {
  const sortedCells = [...cells].sort((a, b) => a.row - b.row || a.col - b.col);
  return {
    grid_split: `${spec.rows}x${spec.cols}`,
    shot_no: sortedCells.map((cell) => `${cell.row}-${cell.col}`).join(','),
  };
}

/**
 * 组装批量执行场景的 params_json：每条任务对应 params_list 中的一项。
 */
export function buildBatchFuncRunParams(parametersList: Record<string, unknown>[]) {
  if (!parametersList.length) {
    return {};
  }
  return { params_list: parametersList };
}

/**
 * 将后链路执行参数序列化为 params_json 字符串。
 */
function serializeFlowFuncRunParamsJson(parameters: Record<string, unknown>) {
  if (!Object.keys(parameters).length) {
    return '';
  }
  try {
    return JSON.stringify(parameters);
  } catch {
    return '';
  }
}

/**
 * 读取后链路面板计费结果，未命中时回退空字符串。
 */
function getFlowFuncRunCost(nodeId: string) {
  const costInfo = useFlowTeamStore.getState().costNodeMap[nodeId];
  return costInfo ? String(getEffectiveNodeCost(costInfo)) : '';
}

/**
 * 解析分镜组埋点上下文：group_id 取组节点 id，group_type 取 data.groupMode（默认 normal）。
 */
export function resolveWorkflowGroupTrackMeta(groupNode: FlowNodeEntity) {
  const groupData = (groupNode.toJSON?.()?.data ?? {}) as { groupMode?: string; };
  return {
    groupId: groupNode.id,
    groupType: groupData.groupMode ?? 'normal',
  };
}

/**
 * 上报功能执行点击（roboneo_func_run_click）：后链路发送、分镜脚本/分镜组操作等。
 */
export function trackFlowFuncRunClick(
  node: FlowNodeEntity,
  options: {
    tab: string;
    paramsJson?: Record<string, unknown>;
    cost?: number | string;
    groupId?: string;
    groupType?: string;
    subTab?: string;
    inputText?: string;
  },
) {
  const groupNode = node.parent?.flowNodeType === FlowNodeBaseType.GROUP
    ? node.parent
    : undefined;
  mtstatTrack('roboneo_func_run_click', {
    room_id: getFlowTeamRoomId(),
    node_id: node.id,
    node_type: node.flowNodeType,
    tab: options.tab,
    input_text: resolveFlowFuncRunInputText(options.paramsJson, options.inputText),
    params_json: serializeFlowFuncRunParamsJson(options.paramsJson ?? {}),
    cost: options.cost === undefined || options.cost === null
      ? getFlowFuncRunCost(node.id)
      : String(options.cost),
    group_id: options.groupId ?? groupNode?.id ?? '',
    group_type: options.groupType
      ?? ((groupNode?.toJSON?.()?.data as { groupMode?: string; } | undefined)?.groupMode ?? 'normal'),
    sub_tab: options.subTab ?? '',
  });
}

/**
 * 汇总工作流底部参数弹窗公共埋点参数，仅保留数据侧要求的节点上下文。
 */
function getFlowInputParamsTrackParams(
  node: FlowNodeEntity,
  nodeType?: string,
) {
  return {
    room_id: getFlowTeamRoomId(),
    node_id: node.id,
    node_type: nodeType ?? node.flowNodeType,
  };
}

/**
 * 上报工作流节点参数选择弹窗曝光。
 */
export function trackFlowInputParamsShow(
  node: FlowNodeEntity,
  nodeType?: string,
) {
  mtstatTrack('roboneo_input_pamas_show', getFlowInputParamsTrackParams(node, nodeType));
}

/**
 * 上报工作流节点参数选择弹窗内的参数变更点击。
 */
export function trackFlowInputParamsClick(
  node: FlowNodeEntity,
  nodeType: string | undefined,
  paramsKey: string,
  paramsValue: string,
) {
  mtstatTrack('roboneo_input_pamas_click', {
    ...getFlowInputParamsTrackParams(node, nodeType),
    params_key: paramsKey,
    params_value: paramsValue,
  });
}

/**
 * 从 media_list 汇总替换埋点用的 URL / 内容地址。
 */
function collectMediaContentUrls(
  mediaList: FlowNodeData['media_list'] | undefined,
) {
  if (!Array.isArray(mediaList)) return '';
  return mediaList
    .map((item) => {
      const media = item as Record<string, unknown>;
      return media.url || media.originUrl || media.uri || media.value;
    })
    .filter(Boolean)
    .map(String)
    .join(',');
}

/**
 * 判断节点在替换上传前是否已有可替换的媒体内容。
 */
function hasReplaceableMedia(mediaList: FlowNodeData['media_list'] | undefined) {
  if (!Array.isArray(mediaList) || !mediaList.length) return false;
  return mediaList.some((item) => {
    const media = item as Record<string, unknown>;
    return Boolean(media.url || media.originUrl || media.uri || media.value);
  });
}

/**
 * 上报节点内容替换（替换按钮触发且上传成功后）。
 */
export function trackFlowNodeReplaceClick(
  node: FlowNodeEntity,
  options: {
    oriUrls: string;
    contentUrls: string;
  },
) {
  mtstatTrack('roboneo_node_replace_click', {
    room_id: getFlowTeamRoomId(),
    node_id: node.id,
    node_type: node.flowNodeType,
    ori_urls: options.oriUrls,
    content_urls: options.contentUrls,
  });
}

/**
 * 替换上传成功且节点原本已有媒体时上报 roboneo_node_replace_click。
 */
export function trackFlowNodeReplaceClickIfNeeded(
  node: FlowNodeEntity,
  previousData: FlowNodeData,
  nextMediaList: FlowNodeData['media_list'],
) {
  if (!hasReplaceableMedia(previousData.media_list)) return;
  trackFlowNodeReplaceClick(node, {
    oriUrls: collectMediaContentUrls(previousData.media_list),
    contentUrls: collectMediaContentUrls(nextMediaList),
  });
}

/**
 * 上报文本内容复制点击，使用文本节点专属事件。
 */
export function trackFlowNodeContentCopyClick() {
  mtstatTrack('roboneo_content_copy_click', {
    room_id: getFlowTeamRoomId(),
  });
}

export type FlowAddChatContentType = 'image' | 'video' | 'audio';

/**
 * 将工作流节点类型映射为「添加到对话」埋点的 content_type。
 */
function resolveFlowAddChatContentType(
  nodeType: string | undefined,
): FlowAddChatContentType | '' {
  if (nodeType === TVFlowNodeNextTypeEnum.IMAGE_NODE) {
    return 'image';
  }
  if (nodeType === TVFlowNodeNextTypeEnum.VIDEO_NODE) {
    return 'video';
  }
  if (nodeType === TVFlowNodeNextTypeEnum.AUDIO_NODE) {
    return 'audio';
  }
  return '';
}

/**
 * 从节点 media_list 首项提取「添加到对话」埋点用的媒体 URL 与 task_id。
 */
function resolveFlowAddChatMediaFields(nodeData: FlowNodeData) {
  const media = nodeData.media_list?.[0] as Record<string, unknown> | undefined;
  const mediaUrl = media?.url || media?.originUrl || media?.uri || '';
  const taskId = media?.task_id || '';
  return {
    mediaUrl: typeof mediaUrl === 'string' ? mediaUrl : String(mediaUrl),
    taskId: typeof taskId === 'string' ? taskId : String(taskId),
  };
}

/**
 * 上报工作流右键「添加到对话」点击（roboneo_add_chat_click）。
 */
export function trackFlowAddChatClick(options: {
  nodeId: string;
  nodeType: string | undefined;
  nodeData: FlowNodeData;
}) {
  const contentType = resolveFlowAddChatContentType(options.nodeType);
  if (!contentType) return;

  const { mediaUrl, taskId } = resolveFlowAddChatMediaFields(options.nodeData);
  if (!mediaUrl.trim()) return;

  mtstatTrack('roboneo_add_chat_click', {
    node_id: options.nodeId,
    room_id: getFlowTeamRoomId(),
    task_id: taskId,
    content_type: contentType,
    media_url: mediaUrl,
  });
}

/**
 * 上报节点内容放大点击。
 */
export function trackFlowNodeBarEnlargeClick(
  node: FlowNodeEntity,
  overrides?: FlowNodeBarTrackOverrides,
) {
  mtstatTrack('roboneo_node_bar_enlarge_click', getFlowNodeBarTrackParams(node, overrides));
}

export type WorkflowHdVipModalFunc =
  | 'change_to_hd'
  | 'change_super_hd'
  | 'change_ai_hd'
  | 'change_4k_video'
  | 'change_ai_4k_video';

const WORKFLOW_HD_VIP_MODAL_POSITION_TYPE = '/team_studio';
const WORKFLOW_HD_VIP_MODAL_FROM_SOURCE = 'workflow_func';

/**
 * 从工作流节点首个媒体项提取 task_id / sub_task_id，供订阅订单 bigdata 透传。
 */
function getWorkflowNodeMediaTaskIds(node: FlowNodeEntity) {
  const media = (node.toJSON?.()?.data as FlowNodeData | undefined)?.media_list?.[0] as Record<string, unknown> | undefined;
  return {
    task_id: typeof media?.task_id === 'string' ? media.task_id : '',
    sub_task_id: typeof media?.sub_task_id === 'string' ? media.sub_task_id : '',
  };
}

/**
 * 组装工作流高清场景唤起订阅弹窗时的 pay_win_show 业务透传参数。
 */
export function buildWorkflowHdVipModalTrackParams(options: {
  node: FlowNodeEntity;
  func: WorkflowHdVipModalFunc;
  apiName: string;
}) {
  const { task_id, sub_task_id } = getWorkflowNodeMediaTaskIds(options.node);
  return {
    from_source: WORKFLOW_HD_VIP_MODAL_FROM_SOURCE,
    position_type: WORKFLOW_HD_VIP_MODAL_POSITION_TYPE,
    room_id: getWorkflowVipModalRoomId(),
    url: typeof window !== 'undefined' ? window.location.href : '',
    node_id: options.node.id,
    func_id: options.func,
    func: options.func,
    api_name: options.apiName,
    task_id,
    sub_task_id,
    orderBigData: {
      from_source: WORKFLOW_HD_VIP_MODAL_FROM_SOURCE,
      func_id: options.func,
      task_id,
      sub_task_id,
    },
  };
}

/**
 * 在工作流图片/视频高清 VIP 档位场景唤起订阅弹窗，并透传 pay_win_show 埋点参数。
 */
export function openWorkflowHdVipModal(options: {
  node: FlowNodeEntity;
  func: WorkflowHdVipModalFunc;
  apiName: string;
}) {
  openVipModal('workflow_func', undefined, buildWorkflowHdVipModalTrackParams(options));
}

/** 资产入库埋点 content_type 与节点类型映射（大写枚举，与数据侧口径一致）。 */
const SAVE_ASSET_CONTENT_TYPE_MAP: Partial<Record<TVFlowNodeNextTypeEnum, string>> = {
  [TVFlowNodeNextTypeEnum.TEXT_NODE]: 'TEXT',
  [TVFlowNodeNextTypeEnum.IMAGE_NODE]: 'IMAGE',
  [TVFlowNodeNextTypeEnum.VIDEO_NODE]: 'VIDEO',
  [TVFlowNodeNextTypeEnum.AUDIO_NODE]: 'AUDIO',
  [TVFlowNodeNextTypeEnum.SCRIPT_NODE]: 'SCRIPT',
};

type SaveAssetUploadLocation = 'history_list' | 'upload' | 'workflow_page';

export interface SaveAssetTrackParams {
  room_id: string;
  node_id: string;
  node_type: string;
  content_type: string;
  media_url: string;
  task_id?: string;
  sub_task_id?: string;
  duration?: string;
  source_from: string;
  location?: string;
}

/**
 * 解析节点内容来源（source_from / content_source）：后链路编辑、工作流执行、上传或 agent 对话。
 * 供资产入库埋点与素材库 ext 持久化共用同一口径。
 */
export function resolveTeamFlowNodeContentSource(node: FlowNodeEntity): ContentSource {
  const data = node.toJSON?.()?.data as (FlowNodeData & { isBacklink?: boolean; }) | undefined;
  if (data?.isBacklink) return 'sed_editing';
  const media = (data?.media_list?.[0] ?? {}) as Record<string, unknown>;
  const source = String([
    media.content_source,
    media.source_from,
    media.source,
    media.node_source,
  ].find((value) => typeof value === 'string' && value.trim()) ?? '').trim();
  if (source === 'agent_teams' || source === 'agent_teams_chat') return 'agent_teams';
  if (source === 'upload') return 'upload';
  if (media.task_id || media.taskId) return 'workflow_run';
  return 'upload';
}

/** 从一组候选字段读取首个非空字符串值（保留与 registerNodeAsset 一致的取值口径）。 */
function pickMediaString(media: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = media[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return '';
}

/** 从多个记录中读取首个非空字符串值，用于兼容 data 与 media_list 上的来源字段。 */
function pickSaveAssetString(records: Array<Record<string, unknown> | undefined>, keys: string[]): string {
  for (const record of records) {
    if (record) {
      const value = pickMediaString(record, keys);
      if (value) return value;
    }
  }
  return '';
}

/** 将保存资产埋点的来源位置收敛到数据侧新增的三个枚举值。 */
function normalizeSaveAssetUploadLocation(value: unknown): SaveAssetUploadLocation | '' {
  const text = String(value ?? '').trim();
  if (text === 'history_list' || text === 'upload' || text === 'workflow_page') {
    return text;
  }
  return '';
}

/** 解析保存资产提交位置：历史记录弹窗、本地上传，其余工作流页内节点统一归到 workflow_page。 */
function resolveSaveAssetUploadLocation(
  node: FlowNodeEntity,
  sourceFrom: ContentSource,
): SaveAssetUploadLocation {
  const data = (node.toJSON?.()?.data ?? {}) as Record<string, unknown>;
  const media = (Array.isArray(data.media_list) ? data.media_list[0] ?? {} : {}) as Record<string, unknown>;
  const customerFileInfo = data.customerFileInfo as Record<string, unknown> | undefined;
  const explicitLocation = normalizeSaveAssetUploadLocation(
    pickSaveAssetString([media, data], ['saveAssetLocation', 'assetSaveLocation', 'location', 'position']),
  );
  if (explicitLocation) return explicitLocation;
  const uploadLocation = pickSaveAssetString([customerFileInfo], ['uploadLocation']);
  if (uploadLocation) return 'upload';
  const mediaLibraryItemId = pickSaveAssetString([media, data], ['mediaLibraryItemId', 'media_library_item_id']);
  if (mediaLibraryItemId) return 'workflow_page';
  const sourceSignal = pickSaveAssetString(
    [media, data],
    ['content_source', 'contentSource', 'source_from', 'sourceFrom', 'source', 'node_source', 'nodeSource'],
  );
  if (sourceSignal === 'history_list') return 'history_list';
  if (sourceFrom === 'upload' || sourceSignal === 'upload') return 'upload';
  return 'workflow_page';
}

/**
 * 汇总资产入库埋点（入口点击 / 提交）公共参数；单条媒体取首项，
 * 字段取值口径与 registerNodeAsset / saveNodeToMediaLibrary 入库链路保持一致
 * （url 优先 originUrl，task_id 兜底 sourceId）。
 */
function getSaveAssetTrackParams(node: FlowNodeEntity, options?: { withLocation?: boolean; }) {
  const data = node.toJSON?.()?.data as FlowNodeData | undefined;
  const media = (data?.media_list?.[0] ?? {}) as Record<string, unknown>;
  const sourceFrom = resolveTeamFlowNodeContentSource(node);
  const params = {
    room_id: getFlowTeamRoomId(),
    node_id: node.id,
    node_type: node.flowNodeType,
    content_type: SAVE_ASSET_CONTENT_TYPE_MAP[node.flowNodeType as TVFlowNodeNextTypeEnum] ?? '',
    media_url: pickMediaString(media, ['originUrl', 'url', 'uri']),
    task_id: pickMediaString(media, ['task_id', 'taskId', 'source_id', 'sourceId']),
    sub_task_id: pickMediaString(media, ['sub_task_id', 'subTaskId']),
    duration: pickMediaString(media, ['duration']),
    source_from: sourceFrom,
  };
  return options?.withLocation
    ? { ...params, location: resolveSaveAssetUploadLocation(node, sourceFrom) }
    : params;
}

/** 上报非 FlowNode 直连场景的保存资产入口点击，供脚本节点内容资产复用统一事件名。 */
export function trackSaveAssetEnterClickByParams(params: SaveAssetTrackParams) {
  mtstatTrack('roboneo_save_enter_click', params);
}

/** 上报非 FlowNode 直连场景的保存资产提交，供脚本节点内容资产复用统一事件名。 */
export function trackSaveAssetUploadByParams(params: SaveAssetTrackParams) {
  mtstatTrack('roboneo_save_asset_upload', params);
}

/**
 * 上报工作流节点「保存资产」入口点击（roboneo_save_enter_click），点击一次上报一次。
 */
export function trackSaveAssetEnterClick(node: FlowNodeEntity) {
  mtstatTrack('roboneo_save_enter_click', getSaveAssetTrackParams(node));
}

/**
 * 上报「保存资产」弹窗提交（roboneo_save_asset_upload），点击保存一次上报一次。
 */
export function trackSaveAssetUpload(node: FlowNodeEntity) {
  mtstatTrack('roboneo_save_asset_upload', getSaveAssetTrackParams(node, { withLocation: true }));
}
